/* eslint-disable no-console -- console.log is needed only in this file */
import * as Sentry from "@sentry/react";
import Pino from "pino";

import { LOG_LEVEL, PWA_ENV } from "~/utils/env";
import { getAuth } from "~/providers/store/auth";
import { getUserIdentifier } from "~/providers/store/employee";
import { getErrorMessage } from "@repo/system";

const DEFAULT_LOG_LEVEL: Pino.Level = "error";
const MESSAGE_KEY = "message";

type LogProperties = Partial<{
  till_id: string | null;
  device_id: string | null;
}> &
  Record<string, unknown>;
type ErrorTags = Record<string, string | boolean | number>;
type LogObject = {
  level: Pino.Level;
  [MESSAGE_KEY]: string;
} & LogProperties;

/**
 * Logger class to handle logging with different levels and outputs.
 * It uses Pino for logging and Sentry Logs for log aggregation.
 */
class Logger {
  private logLevel: Pino.Level;
  private pinoLogger: Pino.Logger;

  /**
   * Creates an instance of Logger.
   * @param level - The log level to use. Defaults to `DEFAULT_LOG_LEVEL`.
   */
  constructor(level?: Pino.Level) {
    this.logLevel = level || DEFAULT_LOG_LEVEL;

    this.pinoLogger = Pino({
      level: this.logLevel,
      messageKey: MESSAGE_KEY,
      browser: {
        formatters: {
          level(label) {
            return { level: label };
          }
        },
        asObject: true,
        write: (logObject) => {
          const { level, [MESSAGE_KEY]: message } = logObject as LogObject;
          const { tillId, status, token } = getAuth();
          const userIdentifier = getUserIdentifier();

          const callerProperties = logObject as LogObject;

          const enrichedLogObject: LogObject = {
            ...callerProperties,
            env: PWA_ENV,
            // A caller-supplied value takes precedence over the auth store,
            // which after `logoutTill` holds none; a caller's explicit null
            // still falls back, since null never identifies a till.
            till_id: callerProperties.till_id ?? tillId,
            ...(status !== "none" && {
              auth_type: token === null ? "id-token" : "session-cookie"
            }),
            ...(userIdentifier && { user_identifier: userIdentifier }),
            ...(userIdentifier?.type === "child-id" && {
              child_id: userIdentifier.value
            })
          };

          this.consoleLog({ ...enrichedLogObject }); // Log to console
          if (PWA_ENV !== "development") {
            // Log to Sentry
            const {
              level: _level,
              [MESSAGE_KEY]: _message,
              ...attributes
            } = enrichedLogObject;
            Sentry.logger[level](message, attributes);
          }
        }
      }
    });
  }

  /**
   * Logs a message to the console based on the log level.
   * @param logObject - The log object containing the log level and message.
   */
  private consoleLog(logObject: LogObject): void {
    const logMessage = `[${logObject.level}] ${logObject[MESSAGE_KEY]}`;

    if (this.shouldLogToConsole(logObject.level)) {
      switch (logObject.level) {
        case "trace":
          console.log(logMessage, logObject);
          break;
        case "debug":
          console.log(logMessage, logObject);
          break;
        case "info":
          console.info(logMessage, logObject);
          break;
        case "warn":
          console.warn(logMessage, logObject);
          break;
        case "error":
          console.error(logMessage, logObject);
          break;
        default:
          console.log(logMessage, logObject);
      }
    }
  }

  /**
   * Determines if the log should be output to the console based on the environment and log level.
   * @param level - The log level.
   * @returns `true` if the log should be output to the console, otherwise `false`.
   */
  private shouldLogToConsole(level: Pino.Level): boolean {
    return PWA_ENV !== "production" || ["error", "warn"].includes(level);
  }

  /**
   * Logs a trace message.
   * @param message - The message to log.
   * @param properties - Additional properties to log with the message.
   */
  trace(message: string, properties: LogProperties = {}): void {
    this.pinoLogger.trace(properties, message);
  }

  /**
   * Logs a debug message.
   * @param message - The message to log.
   * @param properties - Additional properties to log with the message.
   */
  debug(message: string, properties: LogProperties = {}): void {
    this.pinoLogger.debug(properties, message);
  }

  /**
   * Logs an info message.
   * @param message - The message to log.
   * @param properties - Additional properties to log with the message.
   */
  info(message: string, properties: LogProperties = {}): void {
    this.pinoLogger.info(properties, message);
  }

  /**
   * Logs a warning message.
   * @param message - The message to log.
   * @param properties - Additional properties to log with the message.
   */
  warn(message: string, properties: LogProperties = {}): void {
    this.pinoLogger.warn(properties, message);
  }

  /**
   * Logs an error message.
   * @param err - The error to log.
   * @param message - An optional message to log with the error.
   * @param properties - Additional properties to log with the error.
   * @param tags - Optional tags to associate with the error.
   */
  error(
    err: unknown,
    message?: string,
    properties: LogProperties = {},
    tags?: ErrorTags
  ): void {
    this.pinoLogger.error(
      { err, ...properties },
      message || getErrorMessage(err)
    );

    Sentry.captureException(err, { tags, extra: { message, ...properties } });
  }

  /**
   * Flushes the Sentry buffer, ensuring all logs are sent.
   */
  async flush(): Promise<void> {
    await Sentry.flush();
  }
}

export const logger = new Logger(LOG_LEVEL);
